import { call, put } from 'redux-saga/effects';
import { takeLatest } from '../../../utils/reduxSaga';
import orderBy from 'lodash.orderby';
import uniqBy from 'lodash.uniqby';

import { JSONAPI_URLS, OUTSIDE_API } from '../../../consts/endpoints';
import { actions as reduxActions } from '../reducers';
import { actions as sagaActions, GET_USERS } from './types';
import { actions as loaderReducActions } from '../../../partials/loader/reducers';
import {
  doesArrayHasLength,
  isDefined,
  isUndefined,
  isObject
} from '../../../utils/isDefined';
import getFullTableData, {
  altGetFullTableData
} from '../../../utils/getFullTableData';
import { translate } from '../../../i18n/I18nProvider';
import Optional from '../../../utils/optional';

export function getUsers(connectClientId, clientId) {
  return {
    type: sagaActions[GET_USERS],
    connectClientId,
    clientId
  };
}

export function* fetchUsers({ connectClientId, clientId }) {
  const QUERY_PARAMS = {
    sort: 'name',
    include: ['roles', 'sim_cards'],
    fields: { roles: 'name' },
    filter: { client_id: { eq: clientId } }
  };

  if (isUndefined(connectClientId)) {
    yield put(reduxActions.setUsers([]));
  } else {
    yield put(loaderReducActions.startSpinLoader());

    let mappedUsers = [];
    let dirFields = [];
    let filtersOptions = {};
    try {
      const telephonyUsers = yield call(
        altGetFullTableData,
        `${OUTSIDE_API.CLIENTS_V2}/${connectClientId}/users`,
        {},
        200,
        true
      );

      const users = yield call(
        getFullTableData,
        JSONAPI_URLS.USERS,
        QUERY_PARAMS
      );

      mappedUsers = yield call(mapUsers, { telephonyUsers, users });

      filtersOptions = yield call(mapTableSelectOptions, mappedUsers);

      if (doesArrayHasLength(mappedUsers)) {
        dirFields = yield call(mapDirFields, { mappedUsers });
      }
    } catch (error) {
      console.error('Error fetching telephony users:', error);
    } finally {
      yield put(reduxActions.setUsers(mappedUsers, dirFields, filtersOptions));
      yield put(loaderReducActions.stopSpinLoader());
    }
  }
}

function* watchGetTelephonyUsers() {
  yield takeLatest(sagaActions[GET_USERS], fetchUsers);
}

export default watchGetTelephonyUsers();

const fieldsIdArray = [
  'field1',
  'field2',
  'field3',
  'field4',
  'field5',
  'field6'
];

export function mapUsers({ telephonyUsers = [], users = [] }) {
  const mapped = telephonyUsers.map(telephonyUser => {
    const foundedUser = users.find(
      user => user.connect30_user_id === telephonyUser.username
    );

    const licenceType = findLicenseType(telephonyUser);
    const dirFieldsDTOs = telephonyUser?.userDirectoryFieldsDTOs ?? {};

    if (isUndefined(foundedUser)) {
      return { ...telephonyUser, licenceType, simCards: [], fields: [] };
    }

    const {
      id,
      locked,
      locked_at,
      created_at,
      updated_at,
      client_id,
      roles,
      sim_cards = []
    } = foundedUser;

    const simCards = sim_cards.filter(
      card => isObject(card) && isDefined(card.msisdn)
    );

    const mappedFields = mapFields(dirFieldsDTOs);

    const filteredFields = Object.keys(dirFieldsDTOs).reduce((arr, fieldId) => {
      if (!fieldsIdArray.includes(fieldId)) {
        arr = [
          ...arr,
          {
            id: fieldId,
            name: dirFieldsDTOs[fieldId].fieldName,
            value: dirFieldsDTOs[fieldId].fieldValue
          }
        ];
      }
      return arr;
    }, []);

    const dirFieldsforEdit = Object.keys(dirFieldsDTOs).reduce(
      (arr, fieldId) => {
        arr = [...arr, { ...dirFieldsDTOs[fieldId] }];

        return arr;
      },
      []
    );

    const {
      userDeviceActivationStatuses = {},
      firstName,
      lastName
    } = telephonyUser;

    return {
      ...telephonyUser,
      id,
      locked,
      locked_at,
      created_at,
      updated_at,
      client_id,
      roles,
      licenceType,
      simCards,
      name: `${firstName} ${lastName}`,
      mobileApp: Object.keys(userDeviceActivationStatuses).includes('MOBILE'),
      dirFields: Optional(dirFieldsforEdit).or([]),
      fields: filteredFields,
      ...mappedFields,
      detailViewData: { telephonyUser, user: foundedUser }
    };
  });

  return orderBy(mapped, ['name']);
}

export function mapDirFields({ mappedUsers }) {
  const clientDirFields = mappedUsers[0]?.userDirectoryFieldsDTOs;

  return orderBy(clientDirFields, ['fieldId']).map(field => {
    return { id: field.fieldId, name: field.fieldName, value: '' };
  });
}

export function mapTableSelectOptions(data) {
  const department = createSelectOptions({ data, key: 'department' });
  const adminRole = createSelectOptions({ data, key: 'adminRoleId' });
  const userRole = createSelectOptions({ data, key: 'userRoleId' });
  const licenceType = {
    UC: translate('USER.LICENSE_UC'),
    LU: translate('USER.LIGHT_USER'),
    BL: translate('USER.BASIC_USER')
  };

  return { department, adminRole, userRole, licenceType };
}

function createSelectOptions({ data, key }) {
  return orderBy(uniqBy(data, key), key).reduce((obj, el) => {
    const value = el[key];
    if (isUndefined(value)) {
      return { ...obj, null: '-' };
    }
    return { ...obj, [value]: value };
  }, {});
}

export const findLicenseType = data => {
  const isMobileAppEnabled = Object.keys(
    data?.userDeviceActivationStatuses ?? {}
  ).includes('MOBILE');
  if (data.licenceTypeKey === 'UC') {
    return data.licenceTypeKey;
  } else {
    if (!data.softphoneEnabled && !isMobileAppEnabled) {
      return 'LU';
    } else {
      return 'BL';
    }
  }
};

export const mapFields = (fields = {}) => {
  const fieldsIdArray = Object.keys(fields);
  return fieldsIdArray.reduce((obj, fieldId) => {
    return {
      ...obj,
      [fieldId]: {
        name: fields[fieldId].fieldName,
        value: fields[fieldId].fieldValue
      }
    };
  }, {});
};
